/**
 * /order — every file in the order, and how each one prints.
 *
 * The FIRST step of the flow and its own front door: it requires nothing in
 * the URL, because the cart lives on the server and is found from the
 * session. That is what lets the header's cart icon point here from a policy
 * page, and what every retired URL redirects to.
 *
 * ONE SCREEN DOES THE WHOLE JOB. Pick several files at once, see them as a
 * list, open the one that needs different options, remove what you did not
 * mean to add. The alternative it replaces walked the customer through
 * upload, review and options once per document, which meant a student with
 * four PDFs went round four times and could not see the four together until
 * the payment screen.
 *
 * THE SERVER HOLDS THE CART. Every mutation answers with the whole priced
 * cart and that response becomes the cache, so this component keeps no copy
 * to drift, nothing to merge, and no way for a slow response to repaint a
 * price for a cart that has since changed.
 *
 * The one exception is a file whose bytes are still moving: it has no id yet,
 * so it cannot be a cart row. Those live in `useUploadQueue` and are listed
 * above the real rows until the server takes them.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { AppShell } from '@/components/layout/AppShell';
import { useSession } from '@/features/auth/sessionContext';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  ErrorState,
  ProgressBar,
  Skeleton,
  SkeletonGroup,
  Spinner,
} from '@/components/ui';
import { Dropzone } from '@/features/upload/Dropzone';
import { ApplyToAll } from '@/features/cart/ApplyToAll';
import { FileRow } from '@/features/cart/FileRow';
import { RowValidationPoller } from '@/features/cart/RowValidationPoller';
import { otherCartDocuments } from '@/features/cart/documents';
import { useUploadQueue } from '@/features/cart/useUploadQueue';
import { PriceBreakdown } from '@/features/pricing/PriceBreakdown';
import { useAnnouncer } from '@/lib/a11y';
import { createCheckoutQuote, removeCartItem, updateCartItem, useCart } from '@/lib/api/cart';
import { usePrintOptions } from '@/lib/api/catalogue';
import { useMeta } from '@/lib/api/meta';
import { queryKeys, useApiMutation } from '@/lib/api/queryClient';
import type { CartItemUpdate, CartOut, PrintOptionOut, QuoteOut } from '@/lib/api/types';
import { formatExtensionList, formatMegabytes, formatPaise } from '@/lib/format';

import { stepUrl } from './steps';
import { useOrderStep } from './useOrderStep';

/** Stable while the catalogue loads, so the rows' memos do not churn. */
const NO_PRINT_OPTIONS: PrintOptionOut[] = [];

export function BasketStep() {
  const { step, stepper, urlState } = useOrderStep();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { announce } = useAnnouncer();
  const meta = useMeta();
  // ONCE FOR THE WHOLE SCREEN. Every row's options panel is open from the
  // start and every one of them names its presets from this list, so a
  // per-row fetch would be ten copies of four rows that change twice a year.
  const printOptions = usePrintOptions();
  const session = useSession();
  // Not until we know who is asking. The cart is answered from the access
  // token when there is one, so a signed-in customer who reloads this page
  // would otherwise be shown — and given — somebody else's empty cart.
  const sessionSettled = session.status !== 'loading';
  const cart = useCart({ enabled: sessionSettled });

  const [applyAllOpen, setApplyAllOpen] = useState(false);
  const [busyItem, setBusyItem] = useState<string | null>(null);
  const [itemError, setItemError] = useState<string | null>(null);
  const [handoverError, setHandoverError] = useState<string | null>(null);

  /**
   * One cart change at a time, in the order they were made.
   *
   * Every reply carries the WHOLE cart, so two edits in flight together means
   * the second reply can be a snapshot the server took before the first was
   * written — and adopting that would un-tick a confirmation the customer had
   * just given. The row's own controls are disabled while it is busy, but two
   * DIFFERENT rows can be touched in the same second: a basket with warnings
   * on two files shows a checkbox on each, and confirming both quickly is the
   * obvious thing to do.
   *
   * Failures do not break the chain — the next edit still runs, and the one
   * that failed reports itself.
   */
  const chain = useRef<Promise<unknown>>(Promise.resolve());
  const serialised = useCallback(<T,>(work: () => Promise<T>): Promise<T> => {
    const next = chain.current.then(work, work);
    chain.current = next.then(
      () => undefined,
      () => undefined,
    );
    return next;
  }, []);

  /** Every mutation answers with the whole cart, so applying one is a swap. */
  const adopt = useCallback(
    (next: CartOut) => queryClient.setQueryData(queryKeys.cart(), next),
    [queryClient],
  );

  // Memoised because `?? []` is a NEW array on every render, which would
  // make every derived memo below recompute each time and defeat the point
  // of having them.
  const items = useMemo(() => cart.data?.items ?? [], [cart.data]);
  const maxDocuments = cart.data?.max_documents ?? meta.data?.basket.max_documents ?? 10;

  const queue = useUploadQueue({
    limits: meta.data?.upload,
    remainingSlots: Math.max(0, maxDocuments - items.length),
    onCartChanged: adopt,
    // One queue for everything that changes the cart, uploads included.
    serialise: serialised,
  });

  const change = useApiMutation<CartOut, { itemId: string; patch: CartItemUpdate }>({
    mutationFn: ({ itemId, patch }) => serialised(() => updateCartItem(itemId, patch)),
    onSuccess: (next) => {
      setItemError(null);
      adopt(next);
    },
    onError: (error) => setItemError(error.message),
  });
  const remove = useApiMutation<CartOut, string>({
    mutationFn: (itemId) => serialised(() => removeCartItem(itemId)),
    onSuccess: (next) => {
      setItemError(null);
      adopt(next);
    },
    onError: (error) => setItemError(error.message),
  });

  // Continue FREEZES the cart into a quote and hands the id to the rest of
  // the flow. Done here rather than on arrival at the next screen so a
  // refusal — a file that turned out not to be printable — is reported on
  // the screen that can still fix it.
  const handover = useApiMutation<QuoteOut, void>({
    mutationFn: () => createCheckoutQuote(),
    onSuccess: (quote) => {
      setHandoverError(null);
      navigate(
        stepUrl('details', {
          ...urlState,
          quote: quote.id,
          upload: quote.items[0]?.upload_id,
        }),
      );
    },
    onError: (error) => setHandoverError(error.message),
  });

  const onChange = useCallback(
    (itemId: string, patch: CartItemUpdate) => {
      setBusyItem(itemId);
      change.mutate({ itemId, patch }, { onSettled: () => setBusyItem(null) });
    },
    [change],
  );

  const onRemove = useCallback(
    (itemId: string, filename: string) => {
      setBusyItem(itemId);
      remove.mutate(itemId, {
        onSettled: () => setBusyItem(null),
        onSuccess: () => announce(`${filename} removed.`),
      });
    },
    [announce, remove],
  );

  /** One re-read when a background validation lands, not one per row. */
  const refreshCart = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.cart() });
  }, [queryClient]);

  const priceable = useMemo(() => items.filter((item) => item.priceable), [items]);
  const waiting = useMemo(() => items.filter((item) => item.reason === 'validating'), [items]);
  const blocked = useMemo(() => items.filter((item) => item.reason === 'rejected'), [items]);
  const unconfirmed = useMemo(
    () => items.filter((item) => item.priceable && !item.acknowledged),
    [items],
  );
  const uploading = queue.rows.length > 0;

  // One rule, no special cases: nothing leaves this screen until every row is
  // ready. Quietly dropping the files we cannot print would be a surprise
  // discovered at the till, and the customer chose those files.
  const blockedReason = (() => {
    if (uploading) return 'Waiting for your files to upload.';
    if (items.length === 0) return 'Add a file to get started.';
    if (blocked.length > 0)
      return `Remove the ${blocked.length === 1 ? 'file' : 'files'} we cannot print.`;
    if (waiting.length > 0) return 'Still checking your files.';
    if (unconfirmed.length > 0) return 'Confirm the warnings on your files.';
    return null;
  })();
  const ready = cart.data?.ready === true && blockedReason === null;

  const total = cart.data?.totals?.total_paise ?? 0;

  if (cart.isError) {
    return (
      <AppShell title={step.title} progress={stepper}>
        <ErrorState error={cart.error} onRetry={() => void cart.refetch()} />
      </AppShell>
    );
  }

  return (
    <AppShell
      title={step.title}
      progress={stepper}
      aside={
        cart.data?.breakdown && cart.data.totals ? (
          <PriceBreakdown breakdown={cart.data.breakdown} totals={cart.data.totals} defaultOpen />
        ) : undefined
      }
      action={
        <Button
          size="lg"
          fullWidth
          disabled={!ready || handover.isPending}
          loading={handover.isPending}
          onClick={() => handover.mutate(undefined)}
        >
          {total > 0 ? `Continue · ${formatPaise(total)}` : 'Continue'}
        </Button>
      }
    >
      {/* Mounted per unvalidated row. Draws nothing; its whole job is to
          notice when a background check finishes and ask for the cart again. */}
      {waiting.map((item) => (
        <RowValidationPoller
          key={item.upload_id}
          uploadId={item.upload_id}
          onSettled={refreshCart}
        />
      ))}

      <Card
        title="Your files"
        flush
        aside={
          items.length > 0 ? (
            <span className="text-sm text-ink-muted">
              {items.length} of {maxDocuments}
            </span>
          ) : undefined
        }
      >
        {!sessionSettled || cart.isLoading ? (
          <SkeletonGroup label="Loading your files">
            <div className="space-y-3 px-4 py-4">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          </SkeletonGroup>
        ) : items.length === 0 && queue.rows.length === 0 ? (
          <div className="px-4 py-6">
            <EmptyState
              title="Nothing to print yet"
              description="Choose the PDFs or Word documents you want printed. You can add several at once."
            />
          </div>
        ) : (
          <ul>
            {/* Only once there is more than one file to set: with a single
                file the row's own panel is both shorter and more precise. */}
            {priceable.length > 1 ? (
              <li className="border-b border-line px-4 py-3">
                <Button
                  variant="secondary"
                  // Not while bytes are still moving: a file that joins the
                  // cart after the change would quietly keep the defaults,
                  // and "set options for ALL files" would have been a lie
                  // about the one the customer was still waiting for.
                  disabled={uploading}
                  onClick={() => setApplyAllOpen(true)}
                >
                  Set options for all files
                </Button>
              </li>
            ) : null}

            {queue.rows.map((row) => (
              <li key={row.key} className="border-b border-line px-4 py-3 last:border-b-0">
                <div className="flex items-center justify-between gap-3">
                  <span className="min-w-0 truncate text-sm font-medium text-ink">
                    {row.filename}
                  </span>
                  <Button
                    variant="ghost"
                    className="shrink-0 px-2"
                    onClick={() => queue.cancel(row.key)}
                    aria-label={`Cancel ${row.filename}`}
                  >
                    ✕
                  </Button>
                </div>
                {row.status === 'failed' ? (
                  <p className="mt-1 text-xs text-danger">{row.error}</p>
                ) : (
                  <div className="mt-2 flex items-center gap-2">
                    <ProgressBar
                      value={row.status === 'uploading' ? row.percent : undefined}
                      label={`Uploading ${row.filename}`}
                    />
                    {row.status === 'queued' ? (
                      <span className="shrink-0 text-xs text-ink-subtle">Waiting</span>
                    ) : null}
                  </div>
                )}
              </li>
            ))}

            {items.map((item) => (
              <FileRow
                key={item.id}
                item={item}
                printOptions={printOptions.data ?? NO_PRINT_OPTIONS}
                others={otherCartDocuments(cart.data, item.upload_id)}
                coverColours={meta.data?.cover_colours ?? []}
                onChange={(patch) => onChange(item.id, patch)}
                onRemove={() => onRemove(item.id, item.filename)}
                busy={busyItem === item.id}
              />
            ))}
          </ul>
        )}

        <div className="border-t border-line px-4 py-4">
          {items.length + queue.rows.length >= maxDocuments ? (
            <p className="text-sm text-ink-subtle">
              That is the most documents we can put in one order. Anything more needs a second
              order.
            </p>
          ) : (
            <Dropzone
              onFiles={(files) => queue.add(files)}
              accept={(meta.data?.upload.accepted_extensions ?? ['.pdf']).join(',')}
              // READ FROM THE SERVER, never written down here. This is the
              // promise that a file will be accepted, and a hardcoded list
              // starts lying the moment the server's own list changes.
              hint={
                meta.data
                  ? `${formatExtensionList(meta.data.upload.accepted_extensions)} up to ${formatMegabytes(
                      meta.data.upload.max_upload_mb,
                    )}. You can choose several at once.`
                  : 'Loading what we accept…'
              }
              disabled={meta.isLoading}
            />
          )}
        </div>
      </Card>

      <ApplyToAll
        open={applyAllOpen}
        onClose={() => setApplyAllOpen(false)}
        referenceUploadId={priceable[0]?.upload_id}
        coverColours={meta.data?.cover_colours ?? []}
        fileCount={priceable.length}
        onApplied={(result) => adopt(result.cart)}
      />

      {queue.overflow > 0 ? (
        <Alert severity="warning" title="Some files were not added" live className="mt-4">
          <p>
            We could only add {maxDocuments} documents to this order, so {queue.overflow} of the
            files you chose were left out. Place a second order for those.
          </p>
        </Alert>
      ) : null}

      {queue.cooldownUntil !== null ? (
        <Alert severity="warning" title="Too many uploads for now" live className="mt-4">
          <p>
            You have uploaded a lot of files in a short time. The files already in your order are
            safe — wait a moment, then try the rest again.
          </p>
          <p className="mt-2">
            <Button variant="secondary" onClick={queue.retryFailed}>
              Try the rest again
            </Button>
          </p>
        </Alert>
      ) : null}

      {itemError ? (
        <Alert severity="danger" title="We could not change that" live className="mt-4">
          <p>{itemError}</p>
          <p className="mt-2">
            <Button variant="secondary" onClick={() => void cart.refetch()}>
              Reload your files
            </Button>
          </p>
        </Alert>
      ) : null}

      {handoverError ? (
        <Alert severity="danger" title="We could not continue" live className="mt-4">
          <p>{handoverError}</p>
        </Alert>
      ) : null}

      {blockedReason && items.length > 0 ? (
        <p className="mt-4 flex items-center gap-2 text-sm text-ink-muted" role="status">
          {waiting.length > 0 || uploading ? <Spinner size="sm" label="" /> : null}
          {blockedReason}
        </p>
      ) : null}
    </AppShell>
  );
}
